import { useCurrentEditor, useEditorState } from "@tiptap/react";
import { type FC, type RefObject, useCallback, useMemo, useState } from "react";

import { useElementFromRef } from "../../hooks";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../dropdown-menu";
import { Icon } from "../icon";
import { EditorButton } from "./editor-buttons";

interface EditorDropdownMenuTableProps {
  containerRef?: RefObject<HTMLDivElement | null>;
}

export const EditorDropdownMenuTable: FC<EditorDropdownMenuTableProps> = ({ containerRef }) => {
  const container = useElementFromRef(containerRef);
  const [isOpen, setIsOpen] = useState(false);
  const { editor } = useCurrentEditor();

  const editorState = useEditorState({
    editor,
    selector: (ctx) => ({
      canInsertTable: ctx.editor?.can().chain().insertTable().run() ?? false,
      canAddColumnBefore: ctx.editor?.can().chain().addColumnBefore().run() ?? false,
      canAddColumnAfter: ctx.editor?.can().chain().addColumnAfter().run() ?? false,
      canAddRowBefore: ctx.editor?.can().chain().addRowBefore().run() ?? false,
      canAddRowAfter: ctx.editor?.can().chain().addRowAfter().run() ?? false,
      canToggleHeaderColumn: ctx.editor?.can().chain().toggleHeaderColumn().run() ?? false,
      canToggleHeaderRow: ctx.editor?.can().chain().toggleHeaderRow().run() ?? false,
      canToggleHeaderCell: ctx.editor?.can().chain().toggleHeaderCell().run() ?? false,
      canDeleteRow: ctx.editor?.can().chain().deleteRow().run() ?? false,
      canDeleteColumn: ctx.editor?.can().chain().deleteColumn().run() ?? false,
      canDeleteTable: ctx.editor?.can().chain().deleteTable().run() ?? false,
      canMergeCells: ctx.editor?.can().chain().mergeCells().run() ?? false,
      canSplitCell: ctx.editor?.can().chain().splitCell().run() ?? false,
    }),
  });

  const tableOptions = useMemo(
    () => [
      {
        icon: "table" as const,
        label: "Insert table",
        onClick: () => editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(),
        disabled: !editorState?.canInsertTable,
      },
      {
        icon: "add_column_left" as const,
        label: "Add column before",
        onClick: () => editor?.chain().focus().addColumnBefore().run(),
        disabled: !editorState?.canAddColumnBefore,
      },
      {
        icon: "add_column_right" as const,
        label: "Add column after",
        onClick: () => editor?.chain().focus().addColumnAfter().run(),
        disabled: !editorState?.canAddColumnAfter,
      },
      {
        icon: "add_row_above" as const,
        label: "Add row before",
        onClick: () => editor?.chain().focus().addRowBefore().run(),
        disabled: !editorState?.canAddRowBefore,
      },
      {
        icon: "add_row_below" as const,
        label: "Add row after",
        onClick: () => editor?.chain().focus().addRowAfter().run(),
        disabled: !editorState?.canAddRowAfter,
      },
      {
        icon: "splitscreen_left" as const,
        label: "Toggle header column",
        onClick: () => editor?.chain().focus().toggleHeaderColumn().run(),
        disabled: !editorState?.canToggleHeaderColumn,
      },
      {
        icon: "splitscreen_top" as const,
        label: "Toggle header row",
        onClick: () => editor?.chain().focus().toggleHeaderRow().run(),
        disabled: !editorState?.canToggleHeaderRow,
      },
      {
        icon: "tile_small" as const,
        label: "Toggle header cell",
        onClick: () => editor?.chain().focus().toggleHeaderCell().run(),
        disabled: !editorState?.canToggleHeaderCell,
      },
      {
        icon: "delete" as const,
        label: "Delete row",
        onClick: () => editor?.chain().focus().deleteRow().run(),
        disabled: !editorState?.canDeleteRow,
      },
      {
        icon: "delete" as const,
        label: "Delete column",
        onClick: () => editor?.chain().focus().deleteColumn().run(),
        disabled: !editorState?.canDeleteColumn,
      },
      {
        icon: "delete" as const,
        label: "Delete table",
        onClick: () => editor?.chain().focus().deleteTable().run(),
        disabled: !editorState?.canDeleteTable,
      },
      {
        icon: "cell_merge" as const,
        label: "Merge cells",
        onClick: () => editor?.chain().focus().mergeCells().run(),
        disabled: !editorState?.canMergeCells,
      },
      {
        icon: "cell_merge" as const,
        label: "Split cell",
        onClick: () => editor?.chain().focus().splitCell().run(),
        disabled: !editorState?.canSplitCell,
      },
    ],
    [editor, editorState]
  );

  const isDisabled = useMemo(() => tableOptions.every((option) => option.disabled), [tableOptions]);

  const handleCloseAutoFocus = useCallback((event: Event) => {
    event.preventDefault();
  }, []);

  if (!editor) {
    return null;
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <EditorButton icon="table" tooltip="Table" disabled={isDisabled} className="pr-2">
          <Icon icon="keyboard_arrow_down" className="size-4" />
        </EditorButton>
      </DropdownMenuTrigger>

      {/* NOTE: We need to conditionally render the dropdown menu content to ensure the dropdown menu is properly
      positioned with respect to the container ref when the component is mounted or refreshed. */}
      {isOpen && (
        <DropdownMenuContent align="start" onCloseAutoFocus={handleCloseAutoFocus} container={container}>
          {tableOptions.map((option) => (
            <DropdownMenuItem key={option.label} onSelect={option.onClick} disabled={option.disabled}>
              <Icon icon={option.icon} className="text-base" />
              <span className="text-sm">{option.label}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      )}
    </DropdownMenu>
  );
};
